import Bugsnag from "@bugsnag/js";
import client from "../../urql-client";
import { useRouter as usePagesRouter } from "next/router";
import Blank from "pages/_blank";
import { ContentType, getBlogMetaData, isLocaleInStaticRoute, sanitisePostData } from "@utils";
import { AllNewsEventsQuery } from "gql";
import { LatestCaseStudy, getPageTranslations, throttleStaticProps } from "utils/server";
import { YuLifeLocale } from "@hooks";
import { strapiService } from "@services";
import { Logger } from "services/logger";
import { getStrapiLocale } from "utils/locales/strapi";
import { PostData } from "components/resources/resourceArticle/types";
import ResourceArticle from "components/resources/resourceArticle";
import { StaticRoutes } from "@routes";
import { NON_ENGLISH_LOCALES } from "utils/_constants";

export interface ResourceArticleBodyProps {
  postData?: PostData | null;
  latestCaseStudy: LatestCaseStudy;
  locale: YuLifeLocale;
  recommendedResources: AllNewsEventsQuery["newsAndEvents"]["data"][number]["attributes"][];
}

const ResourceArticleBodyComponent: React.FC<ResourceArticleBodyProps> = ({
  postData,
  recommendedResources,
  latestCaseStudy,
  locale,
}) => {
  const router = usePagesRouter();

  if (router.isFallback) {
    return <Blank />;
  }

  return (
    <>
      {getBlogMetaData(postData, locale)}
      <ResourceArticle
        latestCaseStudy={latestCaseStudy}
        recommendedResources={recommendedResources}
        contentType={ContentType.newsEvents}
        postData={postData}
        isCategories={false}
        locale={locale}
      />
    </>
  );
};

export const getStaticPaths = async () => {
  try {
    const allLocaleSlugs = await strapiService.getNewsEventsSlugs();

    return {
      paths: strapiService.formatResourceSlugsForStaticPaths(allLocaleSlugs, true),
      fallback: process.env.ENV_NAME === "local" ? false : "blocking",
    };
  } catch (e) {
    Bugsnag.notify(e);
    return { paths: [], fallback: process.env.ENV_NAME === "local" ? false : "blocking" };
  }
};

export const getStaticProps = async ({ params: { slug }, locale }) => {
  if (!isLocaleInStaticRoute(locale, StaticRoutes.newsAndEvents)) {
    return {
      notFound: true,
    };
  }

  const strapiLocale = getStrapiLocale(locale);
  const isNonEnglishLocale = NON_ENGLISH_LOCALES.includes(locale);
  const includedLocales = isNonEnglishLocale ? [strapiLocale] : ["en", strapiLocale];

  const newsEvents = await strapiService.getNewsAndEventPost(slug, "all", includedLocales);

  if (!newsEvents) {
    Logger.error(`A post cannot be found with slug ${slug} at locale ${locale}`);
    return {
      notFound: true,
    };
  }

  const postData = sanitisePostData(newsEvents);

  const tagSlug = newsEvents.attributes.tags?.data[0]?.attributes?.slug;
  const recommendedResources = await strapiService.getAllPosts(
    "news-and-events",
    "all",
    { start: 0, limit: 3 },
    {
      tags: { slug: { eq: tagSlug } },
      id: { ne: postData.id },
      locale: { in: includedLocales },
    },
  );

  return throttleStaticProps({
    props: {
      postData,
      locale,
      recommendedResources: recommendedResources,
      messages: await getPageTranslations(locale),
    },
    revalidate: 300,
  });
};

export default ResourceArticleBodyComponent;
